(() => {
  if (window.__kenzioPaidSamplesV1) return;
  window.__kenzioPaidSamplesV1 = true;

  const SAMPLE_HANDLE = 'kenzio-paid-sample';
  let cartCache = null;
  let cartCacheAt = 0;
  let cartFetchPromise = null;
  let enhanceTimer = null;
  let normalizing = false;

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;
  };

  async function getCart(force = false) {
    if (!force && cartCache && Date.now() - cartCacheAt < 1200) return cartCache;
    if (cartFetchPromise) return cartFetchPromise;
    cartFetchPromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      })
      .then((cart) => {
        cartCache = cart;
        cartCacheAt = Date.now();
        return cart;
      })
      .finally(() => {
        cartFetchPromise = null;
      });
    return cartFetchPromise;
  }

  function invalidateCart() {
    cartCache = null;
    cartCacheAt = 0;
  }

  function isSampleItem(item) {
    return item?.properties?._kenzio_sample === 'true' || item?.handle === SAMPLE_HANDLE;
  }

  function prop(item, key) {
    return item?.properties?.[key] || '';
  }

  async function normalizeSampleQuantities(cart) {
    if (normalizing) return false;
    const invalid = cart.items
      .map((item, index) => ({ item, index }))
      .find(({ item }) => isSampleItem(item) && Number(item.quantity) !== 1);
    if (!invalid) return false;

    normalizing = true;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: invalid.index + 1, quantity: 1 }),
      });
      invalidateCart();
      return true;
    } catch (error) {
      console.error('[Kenzio Samples] Could not normalize sample quantity', error);
      return false;
    } finally {
      normalizing = false;
    }
  }

  function decorateSampleRow(row, item) {
    if (!row || row.dataset.kzSampleDecorated === item.key) return;
    row.dataset.kzSampleDecorated = item.key;
    row.classList.add('kz-sample-line');

    const originalTitle = prop(item, '_original_product_title') || prop(item, 'Sample Of') || 'Kenzio Product Sample';
    const originalUrl = prop(item, '_original_product_url') || '#';
    const originalVariant = prop(item, '_original_variant_title') || prop(item, 'Selected') || '';
    const originalImage = prop(item, '_original_image_url') || '';

    const nameLink = row.querySelector('.cart-item__name');
    if (nameLink) {
      nameLink.textContent = originalTitle;
      if (originalUrl && originalUrl !== '#') nameLink.href = originalUrl;
    }

    const titleWrap = row.querySelector('.cart-item__title') || row.querySelector('.cart-item__details');
    if (titleWrap && !titleWrap.querySelector('.kz-sample-badge')) {
      const badge = document.createElement('span');
      badge.className = 'kz-sample-badge';
      badge.textContent = 'SAMPLE ORDER';
      titleWrap.insertBefore(badge, titleWrap.firstChild);
    }

    const details = row.querySelector('.cart-item__details');
    if (details && !details.querySelector('.kz-sample-meta')) {
      const meta = document.createElement('div');
      meta.className = 'kz-sample-meta';
      meta.innerHTML = `<strong>1 PC PAID SAMPLE</strong>${originalVariant && originalVariant !== 'Default Title' ? `<span>${escapeHtml(originalVariant)}</span>` : ''}`;
      const titleNode = details.querySelector('.cart-item__title');
      if (titleNode) titleNode.insertAdjacentElement('afterend', meta);
      else details.prepend(meta);
    }

    row.querySelectorAll('.product-option').forEach((option) => {
      const dt = option.querySelector('dt');
      const label = (dt?.textContent || '').replace(':', '').trim().toLowerCase();
      if (['sample price', 'sample of', 'selected', 'order type'].includes(label)) option.style.display = 'none';
    });

    const quantityInput = row.querySelector('input[name="updates[]"]');
    if (quantityInput) {
      quantityInput.value = '1';
      quantityInput.setAttribute('value', '1');
      quantityInput.min = '1';
      quantityInput.dataset.min = '1';
      quantityInput.step = '1';
      quantityInput.disabled = true;
    }
    const quantityControl = quantityInput?.closest('quantity-input');
    if (quantityControl) quantityControl.classList.add('kz-sample-quantity-control');

    const qtyWrapper = row.querySelector('.cart-item__quantity-wrapper');
    if (qtyWrapper && !qtyWrapper.querySelector('.kz-sample-fixed-qty')) {
      const fixed = document.createElement('span');
      fixed.className = 'kz-sample-fixed-qty';
      fixed.textContent = 'QTY 1';
      qtyWrapper.insertBefore(fixed, qtyWrapper.firstChild);
    }

    const mediaCell = row.querySelector('.cart-item__media');
    if (mediaCell && originalImage && !mediaCell.querySelector('img')) {
      const img = document.createElement('img');
      img.className = 'cart-item__image kz-sample-cart-image';
      img.src = originalImage;
      img.alt = originalTitle;
      img.loading = 'lazy';
      img.width = 150;
      mediaCell.appendChild(img);
    }
  }

  async function enhanceCart(force = false) {
    try {
      let cart = await getCart(force);
      if (await normalizeSampleQuantities(cart)) {
        cart = await getCart(true);
        document.querySelector('cart-drawer-items')?.onCartUpdate?.();
        document.querySelector('cart-items')?.onCartUpdate?.();
      }

      cart.items.forEach((item, index) => {
        if (!isSampleItem(item)) return;
        decorateSampleRow(document.getElementById(`CartItem-${index + 1}`), item);
        decorateSampleRow(document.getElementById(`CartDrawer-Item-${index + 1}`), item);
      });
    } catch (error) {
      console.error('[Kenzio Samples] Cart presentation error', error);
    }
  }

  function queueEnhance(force = false) {
    clearTimeout(enhanceTimer);
    enhanceTimer = setTimeout(() => enhanceCart(force), 80);
  }

  class KenzioSampleOrder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.kzReady === 'true') return;
      this.dataset.kzReady = 'true';

      this.button = this.querySelector('[data-kz-order-sample]');
      this.priceEl = this.querySelector('[data-kz-sample-price]');
      this.baseEl = this.querySelector('[data-kz-wholesale-price]');
      this.statusEl = this.querySelector('[data-kz-sample-status]');
      this.productInfo = this.closest('product-info');
      this.productVariants = this.readJson('[data-kz-product-variants]');
      this.sampleVariants = this.readJson('[data-kz-sample-variants]');
      this.lastVariantId = null;
      this.alreadyInCart = false;

      this.onClick = () => this.addSample();
      this.button?.addEventListener('click', this.onClick);

      this.sync();
      this.checkAlreadyInCart();
      this.poller = window.setInterval(() => this.sync(), 400);
    }

    disconnectedCallback() {
      if (this.poller) window.clearInterval(this.poller);
      this.button?.removeEventListener('click', this.onClick);
    }

    readJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '[]');
      } catch (error) {
        console.error('[Kenzio Samples] Invalid variant data', error);
        return [];
      }
    }

    currentVariant() {
      const idInput = this.productInfo?.querySelector('product-form input[name="id"]');
      const id = Number(idInput?.value);
      return this.productVariants.find((variant) => Number(variant.id) === id) || this.productVariants[0] || null;
    }

    matchingSampleVariant(variant) {
      if (!variant) return null;
      const target = Number(variant.price) * 2;
      return this.sampleVariants.find((sample) => Number(sample.price) === target) || null;
    }

    sync() {
      const variant = this.currentVariant();
      if (!variant) {
        this.setUnavailable('Sample unavailable for this product.');
        return;
      }
      const sampleVariant = this.matchingSampleVariant(variant);
      this.lastVariantId = Number(variant.id);
      if (this.baseEl) this.baseEl.textContent = money(variant.price);
      if (this.priceEl) this.priceEl.textContent = money(Number(variant.price) * 2);

      if (!sampleVariant) {
        this.setUnavailable('Sample rate is being updated. Please WhatsApp us for this sample.');
        return;
      }

      if (this.alreadyInCart) {
        this.button.disabled = true;
        this.button.classList.remove('is-loading');
        this.button.innerHTML = '<span>SAMPLE ALREADY IN CART</span><span>✓</span>';
        this.setStatus('Only 1 sample per product can be ordered.', false);
        return;
      }

      this.button.disabled = false;
      this.button.classList.remove('is-loading');
      this.button.innerHTML = '<span>ORDER 1 SAMPLE</span><span>→</span>';
      this.setStatus('1 piece • MOQ does not apply • Sample pricing includes higher setup, development & one-off production costs.', false);
    }

    setUnavailable(message) {
      if (this.button) {
        this.button.disabled = true;
        this.button.classList.remove('is-loading');
        this.button.innerHTML = '<span>SAMPLE UNAVAILABLE</span><span>—</span>';
      }
      this.setStatus(message, true);
    }

    setStatus(message, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message || '';
      this.statusEl.classList.toggle('is-error', Boolean(isError));
    }

    async checkAlreadyInCart(force = false) {
      try {
        const cart = await getCart(force);
        const productId = String(this.dataset.productId || '');
        this.alreadyInCart = cart.items.some(
          (item) => isSampleItem(item) && String(prop(item, '_original_product_id')) === productId
        );
        this.sync();
        return this.alreadyInCart;
      } catch (error) {
        console.error('[Kenzio Samples] Cart check failed', error);
        return false;
      }
    }

    async addSample() {
      if (!this.button || this.button.disabled) return;
      const variant = this.currentVariant();
      const sampleVariant = this.matchingSampleVariant(variant);
      if (!variant || !sampleVariant) return this.setUnavailable('Sample rate is unavailable for this selection.');

      if (await this.checkAlreadyInCart(true)) return;

      this.button.disabled = true;
      this.button.classList.add('is-loading');
      this.button.innerHTML = '<span>ADDING SAMPLE…</span><span>•</span>';
      this.setStatus('Adding 1 paid sample to your cart…', false);

      const originalVariantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const imageUrl = variant.featured_image?.src || variant.featured_image?.url || this.dataset.productImage || '';
      const properties = {
        _kenzio_sample: 'true',
        _original_product_id: String(this.dataset.productId || ''),
        _original_variant_id: String(variant.id),
        _original_product_title: this.dataset.productTitle || '',
        _original_variant_title: originalVariantTitle,
        _original_product_url: this.dataset.productUrl || '',
        _original_image_url: imageUrl,
        'Order Type': 'Sample',
        'Sample Of': this.dataset.productTitle || '',
      };
      if (originalVariantTitle) properties.Selected = originalVariantTitle;

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-drawer', 'cart-icon-bubble'];

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: Number(sampleVariant.id), quantity: 1, properties }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const payload = await response.json();
        if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Could not add sample');

        invalidateCart();
        this.alreadyInCart = true;
        this.sync();

        if (drawer && payload.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(payload);
          if (typeof drawer.open === 'function') drawer.open();
        } else {
          window.location.href = '/cart';
          return;
        }

        queueEnhance(true);
        if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'kenzio-sample-order',
            productVariantId: String(sampleVariant.id),
            cartData: payload,
          });
        }
      } catch (error) {
        console.error('[Kenzio Samples] Add sample failed', error);
        this.alreadyInCart = false;
        this.button.disabled = false;
        this.button.classList.remove('is-loading');
        this.button.innerHTML = '<span>ORDER 1 SAMPLE</span><span>→</span>';
        this.setStatus(error.message || 'Could not add sample. Please try again.', true);
      }
    }
  }

  if (!customElements.get('kenzio-sample-order')) {
    customElements.define('kenzio-sample-order', KenzioSampleOrder);
  }

  const start = () => {
    queueEnhance(true);
    const observer = new MutationObserver(() => queueEnhance(false));
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', (event) => {
      if (event.target.closest('[name="checkout"], #checkout')) queueEnhance(true);
    }, true);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();